import React from 'react';
import type { AuditEventEnvelope } from '@/types';
import { NOT_SIGNED_IN } from './auditMeta';

function initials(name: string | null, email: string | null): string {
  const src = (name || email || '').trim();
  if (!src) return '?';
  if (name) {
    const parts = name.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return name.slice(0, 2).toUpperCase();
  }
  return src.slice(0, 2).toUpperCase();
}

interface ActorChipProps {
  event: Pick<AuditEventEnvelope, 'actorName' | 'actorEmail' | 'actorType' | 'actorCompany'> & { actorUserId?: string | null };
  compact?: boolean;
}

/**
 * Avatar (initials) + name + company tag. Internal = gray "AGI", partner = indigo
 * partner name, system = gray "System". Makes employee vs partner distinct at a glance.
 */
const ActorChip: React.FC<ActorChipProps> = ({ event, compact }) => {
  const isSystem = event.actorType === 'system';
  const isPartner = event.actorType === 'partner';
  // No user and no email: a request that reached the server without a valid sign-in.
  const notSignedIn = !isSystem && !event.actorUserId && !event.actorName && !event.actorEmail;
  const name = isSystem ? 'System' : event.actorName || event.actorEmail?.split('@')[0] || NOT_SIGNED_IN;
  const company = notSignedIn ? null : event.actorCompany || (isSystem ? 'System' : isPartner ? 'Partner' : 'AGI');

  const tagClass = isPartner
    ? 'bg-indigo-100 text-indigo-600'
    : 'bg-gray-100 text-gray-500';

  return (
    <div className="flex items-center gap-1.5 min-w-0">
      <span
        className={`inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[9px] font-semibold ${
          isSystem || notSignedIn ? 'bg-gray-200 text-gray-500' : isPartner ? 'bg-indigo-100 text-indigo-600' : 'bg-blue-100 text-blue-600'
        }`}
        title={event.actorEmail || name}
      >
        {isSystem ? (
          <i className="fas fa-gear text-[9px]" />
        ) : notSignedIn ? (
          <i className="fas fa-user-slash text-[8px]" aria-hidden="true" />
        ) : (
          initials(event.actorName, event.actorEmail)
        )}
      </span>
      <span className="truncate text-gray-700" title={event.actorEmail || name}>
        {name}
      </span>
      {!compact && company && (
        <span className={`shrink-0 rounded px-1 py-0.5 text-[9px] font-medium ${tagClass}`}>{company}</span>
      )}
    </div>
  );
};

export default ActorChip;
